<script setup>
import { useForm } from '@inertiajs/vue3';
import { getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();
const { showLoading, hideLoading } = useLoading();

const selectIcon = (value) => {
    form.icon_name = value;
};

const props = defineProps({
    type: Object,
});

const form = useForm({
    name: props.type.name,
    icon_name: props.type.icon_name,
});

const iconOptions = [
    { label: 'Dice (Casinos)', value: 'pi pi-bitcoin', icon: 'pi pi-bitcoin' },
    { label: 'Heart (Dating)', value: 'pi pi-heart', icon: 'pi pi-heart' },
    { label: 'Shield (Crypto)', value: 'pi pi-shield', icon: 'pi pi-shield' },
    { label: 'Bolt (Bet)', value: 'pi pi-bolt', icon: 'pi pi-bolt' },
    { label: 'Ban (Erotic)', value: 'pi pi-ban', icon: 'pi pi-ban' },
    { label: 'Wallet (Loan)', value: 'pi pi-wallet', icon: 'pi pi-wallet' },
    { label: 'Chart Line (Forex)', value: 'pi pi-chart-line', icon: 'pi pi-chart-line' },
    { label: 'Globe', value: 'pi pi-globe', icon: 'pi pi-globe' },
    { label: 'Star', value: 'pi pi-star', icon: 'pi pi-star' },
    { label: 'Tag', value: 'pi pi-tag', icon: 'pi pi-tag' },
    { label: 'Shopping Cart', value: 'pi pi-shopping-cart', icon: 'pi pi-shopping-cart' },
    { label: 'Briefcase', value: 'pi pi-briefcase', icon: 'pi pi-briefcase' },
    { label: 'Building', value: 'pi pi-building', icon: 'pi pi-building' },
    { label: 'Car', value: 'pi pi-car', icon: 'pi pi-car' },
    { label: 'Graduation Cap', value: 'pi pi-graduation-cap', icon: 'pi pi-graduation-cap' },
    { label: 'Wrench', value: 'pi pi-wrench', icon: 'pi pi-wrench' },
    { label: 'Microphone', value: 'pi pi-microphone', icon: 'pi pi-microphone' },
    { label: 'Camera', value: 'pi pi-camera', icon: 'pi pi-camera' },
    { label: 'Palette', value: 'pi pi-palette', icon: 'pi pi-palette' },
    { label: 'Flag', value: 'pi pi-flag', icon: 'pi pi-flag' },
    { label: 'Trophy', value: 'pi pi-trophy', icon: 'pi pi-trophy' },
    { label: 'Sparkles', value: 'pi pi-sparkles', icon: 'pi pi-sparkles' },
    { label: 'Megaphone', value: 'pi pi-megaphone', icon: 'pi pi-megaphone' },
    { label: 'Hashtag', value: 'pi pi-hashtag', icon: 'pi pi-hashtag' },
    { label: 'Crown', value: 'pi pi-crown', icon: 'pi pi-crown' },
    { label: 'Headphones', value: 'pi pi-headphones', icon: 'pi pi-headphones' },
];

const submit = () => {
    form.put(route('types.update', props.type.id), {
        onStart: () => showLoading('Updating type...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Type updated successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update type',
                life: 5000
            });
        }
    });
};

const deleteType = () => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} "${props.type.name}"? ${$t('This action cannot be undone.')}`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('types.destroy', props.type.id), {
                onStart: () => showLoading('Deleting type...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Type deleted successfully',
                        life: 3000
                    });
                },
                onError: () => {
                    toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete type',
                        life: 5000
                    });
                }
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <ConfirmDialog />

            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-pencil text-2xl"></i>
                            <span>{{ $t('Edit Niche') }}</span>
                        </div>
                    </template>

                    <template #content>
                        <form @submit.prevent="submit">
                            <div class="formgrid grid">
                                <div class="field col-12">
                                    <label for="name" class="font-semibold">{{ $t('Name') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="name"
                                        v-model="form.name"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.name }"
                                        :placeholder="$t('Enter niche name')"
                                        autocomplete="name"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.name" class="p-error text-red-500 font-medium">{{ $t(form.errors.name) }}</small>
                                </div>

                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Icon') }} <span class="text-red-500">*</span></label>
                                    <div class="mt-2 border border-surface-200 rounded-lg p-3">
                                        <div class="flex flex-wrap gap-1">
                                            <button
                                                v-for="opt in iconOptions"
                                                :key="opt.value"
                                                type="button"
                                                v-tooltip.top="opt.label"
                                                @click="selectIcon(opt.value)"
                                                :class="['w-10 h-10 rounded-lg flex items-center justify-center transition-colors', form.icon_name === opt.value ? 'bg-primary-100 text-primary-600 ring-2 ring-primary-400' : 'hover:bg-surface-100 text-surface-700']">
                                                <i :class="opt.icon" class="text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                    <small v-if="form.errors.icon_name" class="p-error text-red-500 font-medium">{{ form.errors.icon_name }}</small>
                                </div>
                            </div>

                            <div class="flex gap-2 mt-4">
                                <Button
                                    type="submit"
                                    :label="$t('Save Changes')"
                                    icon="pi pi-check"
                                    :loading="form.processing"
                                    :disabled="form.processing" />

                                <Button
                                    :label="$t('Cancel')"
                                    icon="pi pi-times"
                                    class="p-button-secondary"
                                    @click="() => { showLoading('Loading types...'); $inertia.visit(route('types.index'), { onFinish: () => hideLoading() }); }"
                                    :disabled="form.processing" />

                                <Button
                                    :label="$t('Delete')"
                                    icon="pi pi-trash"
                                    class="p-button-danger ml-auto"
                                    @click="deleteType"
                                    :disabled="form.processing" />
                            </div>

                            <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                {{ $t('Type updated successfully.') }}
                            </Message>
                        </form>
                    </template>
                </Card>
            </div>

        <div class="col-12 lg:col-4">
            <Card>
                <template #title>
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-info-circle"></i>
                        <span>{{ $t('Niche Information') }}</span>
                    </div>
                </template>
                <template #content>
                    <div class="field-group">
                        <label class="font-semibold text-gray-600">{{ $t('Niche ID:') }}</label>
                        <span class="ml-2">{{ type.id }}</span>
                    </div>

                    <div class="field-group mt-3" v-if="type.sites_count !== undefined">
                        <label class="font-semibold text-gray-600">{{ $t('Associated Sites:') }}</label>
                        <p class="mt-1">
                            <span class="inline-flex align-items-center">
                                <i class="pi pi-globe mr-2"></i>
                                {{ type.sites_count || 0 }} {{ $t('site(s)') }}
                            </span>
                        </p>
                    </div>

                    <div class="field-group mt-3" v-if="type.created_at">
                        <label class="font-semibold text-gray-600">{{ $t('Created') }}:</label>
                        <span class="ml-2">{{ new Date(type.created_at).toLocaleDateString() }}</span>
                    </div>

                    <div class="field-group mt-3" v-if="type.updated_at">
                        <label class="font-semibold text-gray-600">{{ $t('Last Updated:') }}</label>
                        <span class="ml-2">{{ new Date(type.updated_at).toLocaleDateString() }}</span>
                    </div>

                    <div class="mt-4 p-3 bg-yellow-50 border-round">
                        <p class="m-0 text-sm">
                            <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
                            <strong>{{ $t('Warning:') }}</strong> {{ $t('Deleting this type will remove it from all associated sites.') }}
                        </p>
                    </div>
                </template>
            </Card>
        </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}

:deep(#icon_name.p-select) {
    height: 42px;
}

:deep(#icon_name .p-select-label) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.field-group p {
    margin: 0;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}

.bg-yellow-50 {
    background-color: #fef9e7;
}

.text-yellow-600 {
    color: #d68910;
}
</style>